import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Tab } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import type { Category } from "../../../../api/types/accessTypes";
import type { FundDetails, FundStage } from "../../../../api/types/fundStructureTypes";
import type { ObjectClassDefinition, ObjectPermissions } from "../../../../api/types/objectTypes";
import useClientContext from "../../../../context/useClientContext";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import FullHeightFlexPage from "../../../common/layout/FullHeightFlexPage";
import StretchedScrollableContent from "../../../common/layout/StretchedScrollableContent";
import ObjectTitle from "../../../common/ObjectTitle";
import { getDataCatalogueEntityType } from "../../../entityFields/entityTypesHelper";
import EntityCardFiles from "../../../entityFields/files/EntityCardFiles";
import FundCommunicationMatrixTab from "./communication-matrix/FundCommunicationMatrixTab";
import FundDocumentsTab from "./documents/FundDocumentsTab";
import FundMainInfoTab from "./main/FundMainInfoTab";

interface Props {
  fundDetails: FundDetails;
  accessCategories: Category[];
  objectDefinition: ObjectClassDefinition;
  objectPermissions: ObjectPermissions;
  onFundDetailsChange: (details: Partial<FundDetails>) => void;
}

const tabPanelSx = { height: "100%", m: 0, px: 3, pt: 2.5, pb: 0 };

const getFundStageCategories = (stage: FundStage | undefined, accessCategories: Category[]) => {
  switch (stage) {
    case "Investment": {
      return accessCategories.filter((c) => c.type === "Message" || c.type === "Data");
    }
    case "Fundraising": {
      return accessCategories.filter((c) => c.type === "Fundraising");
    }
    default: {
      return [...accessCategories];
    }
  }
};

const FundDetailsPage = ({
  fundDetails,
  accessCategories,
  objectDefinition,
  objectPermissions,
  onFundDetailsChange,
}: Props) => {
  const { tab } = useParams();
  const { hasAnyPermission, isPortalFeatureEnabled } = useClientContext();
  const [selectedTab, setSelectedTab] = useState(tab || "main");

  const hasPermissionsToViewFiles = hasAnyPermission(objectPermissions.filesReadPermissions);
  const hasPermissionsToManageFiles = hasAnyPermission(objectPermissions.filesWritePermissions);

  const { id, name, stage } = fundDetails;

  const onTabChange = (newTab: string) => {
    setSelectedTab(newTab);
    window.history.replaceState({}, document.title, window.location.pathname.replace(/\/[^/]+$/, `/${newTab}`));
  };

  const fundStageCategories = getFundStageCategories(stage, accessCategories);

  return (
    <TabContext value={selectedTab}>
      <FullHeightFlexPage>
        <Box>
          <GeneralPageHeader
            TitleComponent={
              <ObjectTitle
                title={name}
                secondaryTitle={stage ? autoFormatCamelCase(stage) : undefined}
                typeName={objectDefinition.objectName}
              />
            }
            hideBorderLine
            showDefaultBackButtonTo=".."
          />
          <TabList onChange={(_, tab: string) => onTabChange(tab)}>
            <Tab sx={{ ml: 3 }} label="Main Info" value="main" />
            {stage === "Investment" && <Tab label="Investor Documents" value="documents" />}
            {hasPermissionsToViewFiles && isPortalFeatureEnabled("EntityFilesEnabled") && (
              <Tab label="Files" value="files" />
            )}
            <Tab label="Communication Matrix" value="communication-matrix" />
          </TabList>
        </Box>
        <StretchedScrollableContent>
          <TabPanel value="main" sx={{ ...tabPanelSx, backgroundColor: "background.grey" }}>
            <FundMainInfoTab
              fundDetails={fundDetails}
              objectDefinition={objectDefinition}
              objectPermissions={objectPermissions}
              onFundDetailsChange={onFundDetailsChange}
            />
          </TabPanel>
          {stage === "Investment" && (
            <TabPanel value="documents" sx={tabPanelSx}>
              <FundDocumentsTab fundId={id} fundName={name} categories={accessCategories} />
            </TabPanel>
          )}
          {hasPermissionsToViewFiles && isPortalFeatureEnabled("EntityFilesEnabled") && (
            <TabPanel value="files" sx={{ height: "100%", m: 0, px: 0, py: 0 }}>
              <EntityCardFiles
                entityType={getDataCatalogueEntityType(objectDefinition.objectType)}
                entityId={id}
                entityName={name}
                hasPermissionsToManage={hasPermissionsToManageFiles}
              />
            </TabPanel>
          )}
          <TabPanel value="communication-matrix" sx={tabPanelSx}>
            <FundCommunicationMatrixTab fundId={id} categories={fundStageCategories} />
          </TabPanel>
        </StretchedScrollableContent>
      </FullHeightFlexPage>
    </TabContext>
  );
};

export default FundDetailsPage;
